import { Article } from '@wix/answers-api';
import { GenerationLoader } from '@wix/cc-user-facing-common-components';
import { IconButton, Text, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Chat as ChatIcon } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { BI, CHATBOT_IFRAME_URL } from '../../../constants';
import { useArticleUnifiedBi } from '../../../utils/article-unified-bi';
import { useStore } from '../../../zustand/stores';
import { CopilotDisplayMode } from '../../../zustand/stores/copilot';
import {
  createIframeMessageHandler,
  initChatbotParams,
} from '../../ArticleRightSideBar/iframeMessageHandler';
import { UnhelpfulFeedbackChat } from '../../ArticleRightSideBar/UnhelpfulFeedbackChat';
import { useChatbotMessageSender } from '../../ArticleRightSideBar/useChatbotMessageSender';

import css from './index.module.scss';

type ArticleRightPanelProps = {
  article: Article;
  selfArticleRedirect: (url: string, shallow?: boolean, targetLocale?: string) => void;
  isCollapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
  suggestedQuestions?: string[];
};

const noop = () => undefined;

export const ArticleRightPanel = ({
  article,
  selfArticleRedirect,
  isCollapsed,
  onCollapse,
  onExpand,
  suggestedQuestions,
}: ArticleRightPanelProps) => {
  const {
    copilotDisplayMode,
    isChatbotReady,
    hasPendingChatbotMessage,
    setIsChatbotReady,
    setCopilotDisplayMode,
    setChatbotFlow,
    setUnreadMessagesCount,
    chatbotReferral,
    chatbotNodeId,
  } = useStore(state => state.copilotSlice);
  const { selectedSite } = useStore(state => state.siteSelectorSlice);
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { sendMessage } = useChatbotMessageSender({});
  const { helpmatePanelOpenCloseTrack, helpmatePanelMinMaxTrack } =
    useArticleUnifiedBi(article);

  const isLoading = copilotDisplayMode === CopilotDisplayMode.Loading;
  const isUnhelpfulFeedback =
    copilotDisplayMode === CopilotDisplayMode.UnhelpfulFeedback;
  const isActive =
    copilotDisplayMode === CopilotDisplayMode.Chatbot ||
    isLoading ||
    isUnhelpfulFeedback;

  const prevCollapsedRef = useRef(isCollapsed);
  const prevActiveRef = useRef(isActive);
  const prevActiveForBiRef = useRef(isActive);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  if (
    prevCollapsedRef.current !== isCollapsed ||
    prevActiveRef.current !== isActive
  ) {
    prevCollapsedRef.current = isCollapsed;
    prevActiveRef.current = isActive;
    if (!shouldAnimate) {
      setShouldAnimate(true);
    }
  }

  useEffect(() => {
    if (prevActiveForBiRef.current !== isActive) {
      prevActiveForBiRef.current = isActive;
      void helpmatePanelOpenCloseTrack(isActive);
    }
  }, [isActive, helpmatePanelOpenCloseTrack]);

  useEffect(() => {
    if (isLoading && isCollapsed) {
      onExpand();
    }
  }, [isLoading, isCollapsed, onExpand]);

  useEffect(() => {
    if (isChatbotReady && isLoading && !hasPendingChatbotMessage) {
      setCopilotDisplayMode(CopilotDisplayMode.Chatbot);
    }
  }, [
    isChatbotReady,
    isLoading,
    hasPendingChatbotMessage,
    setCopilotDisplayMode,
  ]);

  const handleExpandClick = () => {
    void helpmatePanelMinMaxTrack(false);
    onExpand();
  };

  const handleIframeMessage = useMemo(
    () =>
      createIframeMessageHandler({
        goBack: noop,
        setShowRightSideBar: () => {
          void helpmatePanelMinMaxTrack(true);
          onCollapse();
        },
        setIsChatbotReady,
        selfArticleRedirect,
        setChatbotFlow,
        setUnreadMessagesCount,
        chatbotReferral,
        chatbotNodeId,
        selectedSiteMetaSiteId: selectedSite?.metaSiteId,
        suggestedQuestions,
        chatbotView: BI.CHATBOT_VIEW.HELP_CENTER,
      }),
    [
      onCollapse,
      setIsChatbotReady,
      selfArticleRedirect,
      setChatbotFlow,
      setUnreadMessagesCount,
      chatbotReferral,
      chatbotNodeId,
      selectedSite,
      suggestedQuestions,
      helpmatePanelMinMaxTrack,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [handleIframeMessage]);

  useEffect(() => {
    if (isChatbotReady) {
      initChatbotParams(
        window?.location.href,
        chatbotReferral ?? undefined,
        chatbotNodeId ?? undefined,
        selectedSite?.metaSiteId,
        suggestedQuestions,
        BI.CHATBOT_VIEW.HELP_CENTER,
      );
    }
  }, [suggestedQuestions, isChatbotReady, chatbotReferral, chatbotNodeId, selectedSite]);

  return (
    <div
      className={classNames(css.wrapper, {
        [css.inactive]: !isActive,
        [css.collapsing]: isActive && shouldAnimate && isCollapsed,
        [css.expanding]: isActive && shouldAnimate && !isCollapsed,
        [css.collapsed]: isActive && !shouldAnimate && isCollapsed,
      })}
    >
      {isActive && (
        <>
          <div
            className={classNames(css.iframeContainer, {
              [css.hidden]: isCollapsed || isLoading || isUnhelpfulFeedback,
            })}
          >
            <iframe
              src={CHATBOT_IFRAME_URL}
              className={css.chatbotIframe}
              id="chatbot-iframe"
            />
          </div>
          {isUnhelpfulFeedback && !isCollapsed && (
            <div className={css.unhelpfulFeedbackPanel}>
              <UnhelpfulFeedbackChat
                article={article}
                sendMessage={sendMessage}
                onCollapse={onCollapse}
              />
            </div>
          )}
          {isLoading && !isCollapsed && (
            <div className={css.loadingPanel}>
              <div className={css.loadingHeader}>
                <IconButton
                  size="small"
                  skin="dark"
                  priority="secondary"
                  disabled
                >
                  <span>
                    <Image
                      src="https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/article-page/box.svg"
                      alt="collapse"
                      width={18}
                      height={18}
                    />
                  </span>
                </IconButton>
              </div>
              <div className={css.loadingContent}>
                <GenerationLoader
                  locale={locale as string}
                  className={css.loadingIcon}
                />
                <Text weight="thin" size="tiny">
                  {t('article-page.copilot.loader.txt')}
                </Text>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className={css.collapsedPanel}>
              <Tooltip content={t('article-page.right-panel.tooltip')} placement="top">
                <IconButton
                  size="small"
                  skin="dark"
                  priority="secondary"
                  onClick={handleExpandClick}
                >
                  <ChatIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </div>
  );
};
